import styled from "@emotion/styled";
import { Avatar, Box, Button, CircularProgress, IconButton, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import React from "react";
import { CustomTypography } from "./CustomTypography";
import { Close, Logout, Save, Tune } from "@mui/icons-material";
import { useContext } from "react";
import KeycloakContext from "KeycloakProvider";
import HelpIcon from '@mui/icons-material/Help';
import { useLayoutPreferences } from "./LayoutPreferencesProvider";



const StyledIconButton = styled(IconButton)(({ theme }) => ({
    marginBottom: '0.9rem',
    width: { xs: '25px', sm: '30px' },
    height: { xs: '25px', sm: '30px' },
}));

const headerIconButtonSx = {
    color: '#FFFFFF',
    marginRight: '0.5rem',
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.4)' },
};

const LayoutCustomizationButtons = () => {
    const {
        canCustomize, isEditing, isSaving, isLoading, hasLoadError, startEditing, cancelEditing, save,
    } = useLayoutPreferences();
    if (!canCustomize) return null;

    const customizeLabel = isEditing ? 'Cancel layout customization' : 'Customize menu and home page';
    return (
        <Stack direction="row" alignItems="center" sx={{ marginBottom: "0.8rem" }}>
            <Tooltip title={customizeLabel}>
                <span>
                    <IconButton
                        size="medium"
                        onClick={isEditing ? cancelEditing : startEditing}
                        disabled={isSaving || (!isEditing && (isLoading || hasLoadError))}
                        aria-label={customizeLabel}
                        aria-pressed={isEditing}
                        sx={{
                            ...headerIconButtonSx,
                            backgroundColor: isEditing ? 'rgba(255,255,255,0.2)' : 'transparent',
                        }}
                    >
                        {isEditing ? <Close /> : <Tune />}
                    </IconButton>
                </span>
            </Tooltip>
            {isEditing && (
                <Tooltip title="Save menu and home page preferences">
                    <span>
                        <IconButton
                            size="medium"
                            onClick={save}
                            disabled={isSaving}
                            aria-label="Save layout preferences"
                            sx={headerIconButtonSx}
                        >
                            {isSaving ? <CircularProgress size={22} sx={{ color: '#FFFFFF' }} /> : <Save />}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Stack>
    );
};

const StyledMenu = styled(Menu)({
    marginTop: '45px',
});

export const UserMenu = ({ handleOpenUserMenu, showHelp = false, showUpgardeStatus = false, showExportCsv = false, showNewSystem = false }) => {
    const [anchorElUser, setAnchorElUser] = React.useState(null);
    const { keycloak } = useContext(KeycloakContext);
    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };
    const onClickLogout = () => {
        keycloak.logout({ redirectUri: window.location.origin + '/' });
    };
    return (
        <Box sx={{ flexGrow: 0, marginTop: "0.8rem", display: 'flex', flexDirection: "row", alignItems: "center" }}>
            <Stack direction="row" justifyContent={"center"} alignItems={"center"} sx={{ marginBottom: "0.8rem" }}>
                <Button
                    variant="outlined"
                    component="a"
                    href="mailto:support@nocweb-jmawireless.samanage.com"
                    startIcon={<HelpIcon />}
                    sx={{
                        color: "#FFFFFF",
                        fontSize: "14px",
                        fontWeight: "400",
                        marginRight: "1rem",
                        cursor: "pointer",
                        textTransform: "none",
                    }}
                >
                    Help
                </Button>
            </Stack>

            <LayoutCustomizationButtons />

            <Tooltip title="Open settings">
                <StyledIconButton
                    size="medium"
                    onClick={(event) => {
                        setAnchorElUser(event.currentTarget);
                    }}
                >
                    <Avatar sx={{ fontSize: "25px" }}
                        alt={keycloak.tokenParsed?.preferred_username}
                        src="/static/images/avatar/2.jpg"
                    />
                </StyledIconButton>
            </Tooltip>
            <StyledMenu
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                <MenuItem>
                    <CustomTypography text={`Welcome ${keycloak?.tokenParsed?.preferred_username}`} />
                </MenuItem>
                <MenuItem onClick={onClickLogout}>
                    <Logout />
                    <CustomTypography text="Logout" />
                </MenuItem>
            </StyledMenu>
        </Box>
    );
}